import { useCallback, useEffect, useRef, useState } from 'react';

type UsePollingOptions<T> = {
  requestFn: () => Promise<T>;
  interval?: number;
  maxRuns?: number;
  onError?: (error: unknown) => void;
  onSuccess?: (data: T) => void;
};

export function usePolling<T>({
  requestFn,
  interval = 3000,
  maxRuns = Infinity,
  onError,
  onSuccess,
}: UsePollingOptions<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [runCount, setRunCount] = useState(0);

  const stopRef = useRef(true);
  const runCountRef = useRef(0);

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const start = useCallback(() => {
    if (!stopRef.current) return;

    stopRef.current = false;
    runCountRef.current = 0;
    setRunCount(0); // reset state
    setIsPolling(true);

    const runLoop = async () => {
      while (!stopRef.current && runCountRef.current < maxRuns!) {
        try {
          const response = await requestFn();
          setData(response);
          onSuccess?.(response);
          runCountRef.current += 1;
          setRunCount(runCountRef.current); // update state for UI
        } catch (err) {
          setError(err);
          onError?.(err);
          stop();
          throw err;
        }

        if (!stopRef.current && runCountRef.current < maxRuns!) {
          await delay(interval);
        }
      }

      setIsPolling(false);
    };

    runLoop();
  }, [interval, maxRuns, onError, onSuccess, requestFn]);

  const stop = useCallback(() => {
    stopRef.current = true;
    setIsPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  return { data, error, isPolling, start, stop, runCount };
}
